/**
 * <RecapList> — ce qui va se passer, ou ce qui s'est passé, ligne par ligne (molecule).
 *
 * L'encart des récapitulatifs de parcours : la question « Confirmer votre choix ? » le pose sous son
 * titre (`<ConfirmDialog recap>`), l'accusé sous « C'est fait ». Une ligne par fait, chacune avec sa
 * pastille — verte pour ce qui se fait, orange pour ce qui est proposé et attend une réponse, grise
 * pour le reste — et son fragment en gras en tête (« **Maths du jeudi 18h00 → lundi 18h00**, avec
 * Camille… »), pour qu'on lise la ligne sans la lire en entier.
 *
 * Réutilise `<Card>` pour l'encart (la lavande des encarts, encastrée : pas d'ombre) et
 * `<NotificationDot>` pour la pastille. Aucune copie : les lignes arrivent composées.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { NotificationDot } from "../../atoms/NotificationDot"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { type UIRecapLine } from "../../../ports/primary/ui/types"
import { Card } from "../Card"

const BACKGROUND: SemanticColorKey = "surface-subtle"

const DOT: Record<UIRecapLine["tone"], SemanticColorKey> = {
    success: "success-strong",
    warning: "warning-strong",
    neutral: "text-muted",
}

/** Le diamètre de la pastille : un point de lecture, plus petit qu'un « il y a du neuf ». */
const DOT_SIZE = 8

export type RecapListProps = {
    lines: UIRecapLine[]
    testID?: string
}

export function RecapList(props: RecapListProps) {
    const { lines, testID } = props
    if (lines.length === 0) return null

    return (
        <Card background={BACKGROUND} elevation="none" p="3" testID={testID}>
            <Stack gap="2">
                {lines.map((line, index) => (
                    <Row key={index} gap="2" align="start">
                        <View style={styles.dotCell}>
                            <NotificationDot color={DOT[line.tone]} size={DOT_SIZE} ringColor={BACKGROUND} />
                        </View>
                        <Text size="text-md" color="text-strong" style={styles.text}>
                            {line.lead != null && line.lead !== "" && (
                                <Text size="text-md" weight="bold" color="text-strong">
                                    {line.lead}
                                </Text>
                            )}
                            {line.text ?? ""}
                        </Text>
                    </Row>
                ))}
            </Stack>
        </Card>
    )
}

/** L'interligne des lignes, en proportion de leur taille : la pastille s'aligne sur la première. */
const LINE_RATIO = 1.4

const styles = StyleSheet.create((theme) => ({
    /** La pastille se centre sur la première ligne du texte, pas sur le paragraphe. */
    dotCell: {
        height: Math.round(theme.fontSizes["text-md"] * LINE_RATIO),
        justifyContent: "center" as const,
    },
    text: { flexShrink: 1, lineHeight: Math.round(theme.fontSizes["text-md"] * LINE_RATIO) },
}))
